import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Analisi } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';

interface UseAnalisiReturn {
  analisi: Analisi[];
  loading: boolean;
  error: string | null;
  fetchAnalisi: (condominioId?: string) => Promise<void>;
  deleteAnalisi: (id: string) => Promise<void>;
}

export function useAnalisi(): UseAnalisiReturn {
  const [analisi, setAnalisi] = useState<Analisi[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchAnalisi = useCallback(async (condominioId?: string) => {
    if (!user) {
      setAnalisi([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('analisi')
        .select(`
          *,
          condomini!inner(nome)
        `)
        .order('created_at', { ascending: false });

      if (condominioId) {
        query = query.eq('condominio_id', condominioId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        setError(`Errore caricamento analisi: ${fetchError.message}`);
        setAnalisi([]);
      } else {
        const mappedData = (data || []).map((item: Record<string, unknown>) => ({
          ...item,
          nome_condominio: (item.condomini as Record<string, unknown> | null)?.nome || '',
          // Normalizza nota: usa colonna originale o alias
          nota_sintetica: (item.nota_sintetica as string) || (item.nota_sintesi as string) || '',
          totale_preventivo: (item.totale_preventivo as number) ?? 0,
        })) as Analisi[];
        setAnalisi(mappedData);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Errore inatteso: ${msg}`);
      setAnalisi([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const deleteAnalisi = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        supabase.from('voci_spesa').delete().eq('analisi_id', id),
        supabase.from('analisi_kpi').delete().eq('analisi_id', id),
        supabase.from('analisi_stato_patrimoniale').delete().eq('analisi_id', id),
        supabase.from('analisi_condomini').delete().eq('analisi_id', id),
        supabase.from('analisi_dettaglio_spese').delete().eq('analisi_id', id),
        supabase.from('analisi_raccomandazioni').delete().eq('analisi_id', id),
      ]);

      const { error: delError } = await supabase.from('analisi').delete().eq('id', id);
      
      if (delError) {
        throw new Error(delError.message);
      }

      setAnalisi((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Errore eliminazione: ${msg}`);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    analisi,
    loading,
    error,
    fetchAnalisi,
    deleteAnalisi,
  };
}
